import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { Box, Typography } from '@mui/material';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { SalePhoto } from '../types';

interface ImageCarouselProps {
  photos: SalePhoto[] | string[];
  height?: number | string;
  showDots?: boolean;
  onImageClick?: () => void;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  photos,
  height = 200,
  showDots = true,
  onImageClick,
}) => {
  const getPhotoUrl = (photo: SalePhoto | string): string => {
    if (typeof photo === 'string') {
      return photo;
    }
    return photo.photo_url;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"%3E%3Crect width="100%" height="100%" fill="%23f3f4f6"/%3E%3Ctext x="50%" y="50%" font-family="sans-serif" font-size="14" fill="%239ca3af" text-anchor="middle" dominant-baseline="middle"%3EImage Unavailable%3C/text%3E%3C/svg%3E';
  };

  if (!photos || photos.length === 0) {
    return (
      <Box
        sx={{
          height,
          width: '100%',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No photos available
        </Typography>
      </Box>
    );
  }

  if (photos.length === 1) {
    return (
      <Box
        component="img"
        src={getPhotoUrl(photos[0])}
        alt="Sale photo"
        onError={handleImageError}
        onClick={onImageClick}
        sx={{
          height,
          width: '100%',
          objectFit: 'cover',
          cursor: onImageClick ? 'pointer' : 'default',
        }}
      />
    );
  }

  return (
    <Box sx={{ height, width: '100%', position: 'relative' }}>
      <Swiper
        modules={[Navigation, Pagination]}
        spaceBetween={0}
        slidesPerView={1}
        navigation
        pagination={showDots ? { clickable: true } : false}
        loop={photos.length > 1}
        style={{ height: '100%', width: '100%' }}
      >
        {photos.map((photo, index) => (
          <SwiperSlide key={index}>
            <Box
              component="img"
              src={getPhotoUrl(photo)}
              alt={`Sale photo ${index + 1}`}
              onError={handleImageError}
              onClick={onImageClick}
              sx={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
                cursor: onImageClick ? 'pointer' : 'default',
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      
      {photos.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.6)',
            color: 'white',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 500,
            zIndex: 10,
          }}
        >
          {photos.length} photos
        </Box>
      )}
    </Box>
  );
};

export default ImageCarousel;
